// src/components/ui/date-range-picker.tsx
// IR-1415: promoted from src/features/links/ to components/ui/ unchanged --
// the Data-Management "Added" filter is the second consumer, so the control
// no longer belongs to the Join Links feature. Behaviour is byte-identical;
// only the path and the two imports moved.
// IR-1047 Phase 3.5 — standard range picker (Rotem: one picker, presets +
// calendar, not two date fields). react-day-picker (range mode) + date-fns
// presets; emits yyyy-MM-dd strings (the contract the BE first_seen filter
// already expects). Apply-on-Apply, like the multi-select filters.
import { RangeCalendar } from "@/components/ui/range-calendar";
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import { useEffect, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";

export interface IsoRange {
  from?: string | undefined;
  to?: string | undefined;
}

interface Props {
  label: string;
  value: IsoRange;
  onApply: (next: IsoRange) => void;
}

const ISO = "yyyy-MM-dd";

function toDraft(v: IsoRange): DateRange | undefined {
  if (!v.from && !v.to) return undefined;
  return {
    from: v.from ? parseISO(v.from) : undefined,
    to: v.to ? parseISO(v.to) : undefined,
  };
}

function toIso(r: DateRange | undefined): IsoRange {
  if (!r?.from) return {};
  return { from: format(r.from, ISO), to: r.to ? format(r.to, ISO) : format(r.from, ISO) };
}

// IR-1384 r3: the calendar no longer offers future days, so a preset must not
// hand one back through the side door. "This week/month/year" all run to a
// period end that is still ahead of us — clamped to today, they select exactly
// the days that can actually contain a link and the calendar shading stops at
// the same edge the grid does.
function clampToToday(end: Date, now: Date): Date {
  return end > now ? now : end;
}

export function buildPresets(now: Date = new Date()): { label: string; range: DateRange }[] {
  const day = (d: Date) => ({ from: d, to: d });
  const to = (end: Date) => clampToToday(end, now);
  return [
    { label: "Today", range: day(now) },
    { label: "Yesterday", range: day(subDays(now, 1)) },
    { label: "This week", range: { from: startOfWeek(now), to: to(endOfWeek(now)) } },
    {
      label: "Last week",
      range: { from: startOfWeek(subWeeks(now, 1)), to: endOfWeek(subWeeks(now, 1)) },
    },
    { label: "This month", range: { from: startOfMonth(now), to: to(endOfMonth(now)) } },
    {
      label: "Last month",
      range: { from: startOfMonth(subMonths(now, 1)), to: endOfMonth(subMonths(now, 1)) },
    },
    { label: "This year", range: { from: startOfYear(now), to: to(endOfYear(now)) } },
    {
      label: "Last year",
      range: { from: startOfYear(subYears(now, 1)), to: endOfYear(subYears(now, 1)) },
    },
  ];
}

export function DateRangePicker({ label, value, onApply }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(toDraft(value));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setDraft(toDraft(value));
  }, [value, open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const trigger = value.from || value.to ? `${value.from ?? "…"} – ${value.to ?? "…"}` : label;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className="iris-input flex w-full items-center justify-between gap-2 text-left text-sm"
      >
        <span className={value.from ? "" : "text-[var(--iris-fg-3)]"}>{trigger}</span>
        <span aria-hidden className="text-[var(--iris-fg-4)]">
          ▾
        </span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 flex rounded-md border border-[var(--iris-bg-4)] bg-[var(--iris-bg-1)] p-2 shadow-lg">
          <div className="flex w-32 flex-col gap-0.5 border-r border-[var(--iris-bg-4)] pr-2">
            {buildPresets().map((p) => (
              <button
                key={p.label}
                type="button"
                className="rounded px-2 py-1 text-left text-xs text-[var(--iris-fg-2)] hover:bg-[var(--iris-bg-3)]"
                onClick={() => setDraft(p.range)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="pl-2">
            {/* IR-1384 r2: shared with the Groups "Added" filter — same compact
                dark calendar and the same visible Start/End endpoints. The
                stock react-day-picker theme painted in-range days white on
                near-white here too. */}
            <RangeCalendar draft={draft} onSelect={setDraft} />
            <div className="mt-1 flex items-center justify-between border-t border-[var(--iris-bg-4)] pt-2">
              <button
                type="button"
                className="text-xs text-[var(--iris-fg-3)] hover:text-[var(--iris-fg-1)]"
                onClick={() => setDraft(undefined)}
              >
                Clear
              </button>
              <div className="flex gap-2">
                <button type="button" className="iris-btn text-xs" onClick={() => setOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="iris-btn iris-btn--primary text-xs"
                  onClick={() => {
                    onApply(toIso(draft));
                    setOpen(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
